<template>
  <v-card class="rounded-2" height="100%" :width="cardWidth" elevation="24">
    <!--Card Header-->
    <v-toolbar color="primary" flat>
      <v-container>
        <v-row>
          <v-col class="d-flex" cols="12">
            <v-tooltip location="right">
              <template v-slot:activator="{ props: tooltipProps }">
                <v-toolbar-title
                  class="text-aliceBlue font-weight-bold ml-1 text-h5 mt-2"
                  v-bind="tooltipProps"
                >
                  <v-icon color="white" class="mt-n2" start size="small">
                    mdi-layers
                  </v-icon>
                  {{ siteSystem.siteSystemName }}
                </v-toolbar-title>
              </template>
              <span>{{ siteSystem.siteSystemName }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>
    </v-toolbar>

    <!--Card Body-->
    <v-card-text style="min-height: calc(100% - 130px)">
      <v-row>
        <v-col>
          <span class="text-subtitle-3 text-primary">Site System Type</span>
          <div class="text-primary font-weight-medium">
            {{ siteSystem.siteSystemTypeName }}
          </div>
        </v-col>
      </v-row>
      <v-row v-if="hasEquipment">
        <v-col>
          <span class="text-subtitle-3 text-primary">Equipment</span>

          <!-- Rich view: siteSystem.equipments available (detail endpoint) -->
          <v-expansion-panels
            v-if="siteSystem.equipments?.length"
            class="mt-2"
            variant="accordion"
          >
            <v-expansion-panel
              v-for="eq in siteSystem.equipments"
              :key="eq.equipmentId"
            >
              <template v-slot:title>
                <span class="text-body-2 font-weight-medium">{{
                  eq.equipmentName
                }}</span>
              </template>
              <template v-slot:text>
                <div class="text-body-2 text-medium-emphasis">
                  {{ eq.equipmentTypeName }}
                </div>
                <div
                  v-if="eq.siteLocationName"
                  class="text-caption my-1 text-primary"
                >
                  <v-icon size="small" class="mr-1"
                    >mdi-map-marker-outline</v-icon
                  >
                  {{ eq.siteLocationName }}
                </div>
                <div class="d-flex flex-wrap ga-1 mt-2">
                  <v-chip
                    class="font-weight-bold"
                    size="small"
                    color="primary"
                    variant="elevated"
                    label
                  >
                    {{ eq.endUses.length }}
                    End Use{{ eq.endUses.length !== 1 ? 's' : '' }}
                  </v-chip>
                  <v-chip
                    class="font-weight-bold"
                    size="small"
                    color="secondary"
                    variant="elevated"
                    label
                  >
                    {{ eq.sensors.length }}
                    Sensor{{ eq.sensors.length !== 1 ? 's' : '' }}
                  </v-chip>
                </div>
              </template>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Fallback view: only equipmentIds available (list endpoint) -->
          <v-expansion-panels v-else class="mt-2" variant="accordion">
            <v-expansion-panel
              v-for="item in equipmentItems"
              :key="item.name"
              :title="item.name"
              :disabled="item.disabled"
            />
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-card-text>

    <!--Card Footer-->
    <v-divider />
    <v-card-actions v-if="userCanUpdate || userCanDelete">
      <ActionButtonsVue :buttonsList="actionButtons" />
    </v-card-actions>

    <!--Confirm Dialog-->
    <ConfirmDialogDeleteSiteSystem
      v-if="showConfirmDialog"
      :dialog="showConfirmDialog"
      :siteSystemInfo="siteSystem"
      @update:dialog="(value: boolean) => (showConfirmDialog = value)"
    />
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { useAbility } from '@/plugins/useAbility.ts';
import ActionButtonsVue from '@/Core.UI.Domain/Components/ActionButtons.vue';
import ConfirmDialogDeleteSiteSystem from '@/Core.Service.Domain/Sites/SiteSystems/Components/ConfirmDialogDeleteSiteSystem.vue';

import type { IActionButton } from '@/Core.UI.Domain/Components/types/actionButtons';
import type {
  ISiteSystem,
  ISiteSystemEquipment,
} from '@/Core.Service.Domain/Sites/SiteSystems/types/index.ts';

const props = defineProps<{
  siteSystem: ISiteSystem;
}>();

const route = useRoute();
const router = useRouter();
const store = useStore();
const ability = useAbility();

const showConfirmDialog = ref(false);
const cardWidth = ref(360);

const equipmentForSite = computed<ISiteSystemEquipment[]>(
  () => store.getters['SitesStore/SiteSystemsStore/equipmentForSite']
);

const hasEquipment = computed(
  () =>
    (props.siteSystem.equipments?.length ?? 0) > 0 ||
    (props.siteSystem.equipmentIds?.length ?? 0) > 0
);

const equipmentItems = computed(() =>
  (props.siteSystem.equipmentIds ?? []).map((id) => {
    const equipment = equipmentForSite.value.find((e) => e.equipmentId === id);
    return {
      name: equipment?.equipmentFriendlyName ?? `Equipment ${id}`,
      disabled: !equipment?.description,
    };
  })
);

const userCanUpdate = computed(() => ability.can('update', 'analytics'));
const userCanDelete = computed(() => ability.can('delete', 'analytics'));

const actionButtons: IActionButton[] = [
  {
    icon: 'pencil',
    toolTip: `Edit ${route.meta?.toolTip || 'Site System'}`,
    action: () => {
      router.push({
        name: 'SiteSystemsEdit',
        params: { id: props.siteSystem.siteSystemId },
      });
    },
  },
  {
    icon: 'trash-can-outline',
    toolTip: `Delete ${route.meta?.toolTip || 'Site System'}`,
    action: () => {
      showConfirmDialog.value = true;
    },
  },
];
</script>
